// src/sim/collapse.ts -- pure, no DOM
import type { Terrain } from './terrain';

/**
 * Process one tick of dirt collapse. Returns true if any dirt moved
 * (caller should keep calling until false for full settle, or call
 * once per sim tick for animated cascade).
 *
 * Scans bottom-up so dirt cascades correctly: lower gaps fill first,
 * then higher dirt falls into vacated space next tick.
 */
export function collapseTick(terrain: Terrain): boolean {
  const { width, height } = terrain;
  const d = terrain.buffer.data;
  let anyMoved = false;

  for (let x = 0; x < width; x++) {
    // Scan bottom-up: for each column, find floating dirt and move it down one row
    for (let y = height - 2; y >= 0; y--) {
      const srcIdx = (y * width + x) * 4;
      const dstIdx = ((y + 1) * width + x) * 4;

      // If this pixel is solid and the one below is air, swap them
      if (d[srcIdx + 3] !== 0 && d[dstIdx + 3] === 0) {
        // Copy RGBA from src to dst
        d[dstIdx] = d[srcIdx];
        d[dstIdx + 1] = d[srcIdx + 1];
        d[dstIdx + 2] = d[srcIdx + 2];
        d[dstIdx + 3] = d[srcIdx + 3];
        // Clear src
        d[srcIdx + 3] = 0;
        anyMoved = true;
      }
    }
  }

  if (anyMoved) {
    terrain.markDirtyFull(); // entire terrain could have shifted
  }
  return anyMoved;
}

/**
 * Settle a tank onto the terrain surface after collapse.
 * Returns fall distance in pixels (for future fall damage).
 */
export function settleTank(
  tank: { x: number; y: number; alive: boolean },
  terrain: Terrain,
): number {
  const startY = tank.y;

  // Scan downward from bottom of tank hitbox to find terrain surface
  let y = tank.y + 4;
  while (y < terrain.height && !terrain.isSolid(tank.x, y)) {
    y++;
  }

  // New tank position: 4px above the surface (tank height offset)
  if (y >= terrain.height) {
    // No terrain below -- park at world bottom
    tank.y = terrain.height - 4;
  } else {
    tank.y = y - 4;
  }

  return Math.max(0, tank.y - startY);
}
